<template>
  <div class="container mt-5">
    <div class="card success-card">
      <div class="card-body text-center py-5">
        <div v-if="loading" class="loading-container">
          <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
          <h4>{{ loadingTitle }}</h4>
          <p class="text-muted">{{ retryMessage }}</p>
        </div>

        <div v-else-if="error" class="error-container">
          <div class="icon-wrapper error-icon mb-4">
            <i class="fas fa-exclamation-circle"></i>
          </div>
          <h2 class="mb-3">Une erreur est survenue</h2>
          <p class="text-muted mb-4">{{ error }}</p>
          <div class="mt-4">
            <button @click="retryVerification" class="btn btn-primary me-3">
              <i class="fas fa-redo me-2"></i>
              Réessayer
            </button>
            <router-link to="/liste-besc" class="btn btn-outline-secondary">
              <i class="fas fa-arrow-left me-2"></i>
              Retour à la liste
            </router-link>
          </div>
        </div>

        <div v-else class="success-container">
          <div class="icon-wrapper success-icon mb-4">
            <i class="fas fa-check-circle"></i>
          </div>

          <h2 class="mb-3">Paiement réussi !</h2>

          <div class="success-details mb-4">
            <p class="lead mb-4">
              Votre BESC <strong>{{ refbesc }}</strong> a été payé avec succès
              et soumis pour validation.
            </p>

            <div class="info-cards">
              <div class="info-card">
                <i class="fas fa-credit-card mb-2"></i>
                <div class="info-label">Moyen de paiement</div>
                <div class="info-value">{{ paymentMethodLabel }}</div>
              </div>

              <div class="info-card">
                <i class="fas fa-file-invoice-dollar mb-2"></i>
                <div class="info-label">Référence</div>
                <div class="info-value">{{ refbesc }}</div>
              </div>

              <div class="info-card">
                <i class="fas fa-clock mb-2"></i>
                <div class="info-label">Statut</div>
                <div class="info-value">
                  <span :class="getStatusClass(bescStatus?.code)">
                    {{ bescStatus?.label || '—' }}
                  </span>
                </div>
              </div>
            </div>

            <div class="alert alert-info mt-4">
              <i class="fas fa-info-circle me-2"></i>
              Votre BESC sera traité par nos équipes dans les plus brefs délais.
              Vous recevrez une notification par email.
            </div>
          </div>

          <div class="action-buttons">
            <router-link to="/liste-besc" class="btn btn-primary btn-lg">
              <i class="fas fa-list me-2"></i>
              Retourner à la liste de mes BESCs
            </router-link>

            <!-- <button 
              @click="downloadReceipt" 
              class="btn btn-outline-secondary btn-lg ms-3"
              :disabled="downloadingReceipt">
              <i v-if="downloadingReceipt" class="fa fa-spinner fa-spin me-2"></i>
              <i v-else class="fas fa-download me-2"></i>
              Télécharger le reçu
            </button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError, success as showSuccess } from '@/utils/utils';

export default defineComponent({
  name: 'PaiementSuccess',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const loading = ref(true);
    const error = ref('');
    const refbesc = ref('');
    const bescId = ref('');
    const paymentMethod = ref('');
    const transactionId = ref('');
    const downloadingReceipt = ref(false);
    const retryMessage = ref('Veuillez patienter...');
    const loadingTitle = ref('Vérification du paiement...');
    const bescStatus = ref<{ code: string; label: string } | null>(null)


    const paymentMethodLabel = computed(() => {
      switch (paymentMethod.value) {
        case 'stripe': return 'Carte Bancaire (Stripe)';
        case 'kkiapay': return 'Mobile Money (KKiaPay)';
        case 'paypal': return 'PayPal';
        default: return 'Non spécifié';
      }
    });

    //  NOUVELLE FONCTION : Capturer le paiement PayPal
    const capturePayPalPayment = async (orderID: string): Promise<boolean> => {
      try {
        console.log(' Capture du paiement PayPal...');
        console.log(' OrderID:', orderID);

        loadingTitle.value = 'Finalisation du paiement PayPal...';
        retryMessage.value = 'Veuillez patienter quelques instants';

        // Récupérer le commentaire depuis sessionStorage (optionnel)
        const comment = sessionStorage.getItem(`besc_${bescId.value}_comment`) || '';

        const { data } = await ApiService.post(`/besc/${bescId.value}/payment/paypal/capture`, { orderID });

        if (data.data.paymentConfirmed || data.success) {
          console.log(' Paiement PayPal capturé avec succès');
          sessionStorage.removeItem(`besc_${bescId.value}_comment`);
          return true;
        }

        return false;
      } catch (err: any) {
        console.error(' Erreur capture PayPal:', err);
        throw err;
      }
    };

    const checkPaymentStatus = async (maxRetries = 8, delayMs = 2000): Promise<boolean> => {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Tentative ${attempt}/${maxRetries} de vérification...`);
          retryMessage.value = `Vérification ${attempt}/${maxRetries}...`;

          const { data } = await ApiService.get(`/besc/${bescId.value}/check-payment`);
          // ou `/besc/${bescId.value}` si tu utilises directement ça

          if (data.data.isPaid) {
            console.log('BESC payé confirmé !');
            refbesc.value = data.data.refbesc || data.data.ref;
            bescStatus.value = data.data.currentStatus || null
            return true;
          }

          console.log(`BESC pas encore payé (tentative ${attempt})`);

          if (attempt < maxRetries) {
            const waitTime = delayMs * attempt; // délai croissant : 2s, 4s, 6s...
            retryMessage.value = `Nouvelle vérification dans ${waitTime / 1000}s...`;
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        } catch (err: any) {
          console.error(`Erreur tentative ${attempt}:`, err);
          if (attempt === maxRetries) throw err;
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      return false;
    };

    const verifyPaymentAndBesc = async () => {
      try {
        bescId.value = route.query.bescId as string;
        paymentMethod.value = route.query.method as string;
        transactionId.value = route.query.transactionId as string || '';
        const paypalToken = route.query.token as string;

        if (!bescId.value) {
          error.value = 'Informations de paiement manquantes';
          loading.value = false;
          return;
        }

        console.log(' Vérification du BESC:', bescId.value);
        console.log(' Méthode:', paymentMethod.value);

        //  SI PAYPAL : CAPTURER D'ABORD LE PAIEMENT
        if (paymentMethod.value === 'paypal' && paypalToken) {
          console.log(' Détection d\'un retour PayPal, capture du paiement...');

          const captured = await capturePayPalPayment(paypalToken);

          if (!captured) {
            error.value = 'Erreur lors de la capture du paiement PayPal';
            loading.value = false;
            return;
          }
        }

        //  VÉRIFIER LE STATUT (pour tous les modes de paiement)
        loadingTitle.value = 'Vérification du paiement...';
        const isPaid = await checkPaymentStatus();

        if (isPaid) {
          console.log(' Paiement confirmé');
          loading.value = false;
        } else {
          console.warn(' BESC toujours non payé après vérification');
          error.value = 'Le paiement n\'a pas pu être confirmé. Veuillez contacter le support.';
          loading.value = false;
        }

      } catch (err: any) {
        console.error(' Erreur:', err);
        error.value = err.response?.data?.message || 'Erreur lors de la vérification du paiement';
        loading.value = false;
      }
    };

     const getStatusClass = (code: string) => {
  switch (code) {

    case 'DRAFT':
      return 'badge bg-secondary';

    case 'SUBMITTED':
      return 'badge bg-warning text-dark';

    case 'CORRECTIONS_IN_PROGRESS':
      return 'badge bg-info';

    case 'CORRECTIONS_COMPLETED':
      return 'badge bg-primary';

    case 'APPROVED':
      return 'badge bg-success';

    case 'ISSUED':
      return 'badge bg-primary';

    case 'REJECTED':
      return 'badge bg-danger';

    case 'CANCELLED':
      return 'badge bg-dark';

    default:
      return 'badge bg-secondary';
  }
};


    const retryVerification = () => {
      loading.value = true;
      error.value = '';
      verifyPaymentAndBesc();
    };

    const downloadReceipt = async () => {
      try {
        downloadingReceipt.value = true;

        const response = await ApiService.getFile(`/besc/${bescId.value}/payment-receipt`);

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Recu_Paiement_${refbesc.value}.pdf`);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          link.remove();
          window.URL.revokeObjectURL(url);
        }, 100);

        showSuccess('Reçu téléchargé avec succès !');

      } catch (err: any) {
        console.error(' Erreur téléchargement reçu:', err);
        showError('Le reçu n\'est pas encore disponible. Réessayez dans quelques instants.');
      } finally {
        downloadingReceipt.value = false;
      }
    };

    onMounted(async () => {
      console.log(' Page de succès chargée');
      console.log(' Query params:', route.query);

      await verifyPaymentAndBesc();
    });

    return {
      loading,
      error,
      refbesc,
      paymentMethod,
      paymentMethodLabel,
      downloadReceipt,
      downloadingReceipt,
      retryMessage,
      loadingTitle,
      retryVerification,
      bescStatus,
      getStatusClass
    };
  }
});
</script>

<style scoped>
.success-card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.icon-wrapper {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
}

.success-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  animation: scaleIn 0.5s ease-out;
}

.error-icon {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}

h2 {
  font-weight: 700;
  color: #1e293b;
}

.lead {
  font-size: 1.1rem;
  color: #475569;
}

.info-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
}

.info-card {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  border-color: #0a59a4;
}

.info-card i {
  font-size: 2rem;
  color: #0a59a4;
}

.info-label {
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.5rem;
  font-weight: 500;
}

.info-value {
  font-size: 1rem;
  color: #1e293b;
  font-weight: 700;
  margin-top: 0.5rem;
}

.action-buttons {
  margin-top: 2rem;
}

.btn-lg {
  padding: 0.875rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #0a59a4 0%, #0d6ec9 100%);
  border: none;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(10, 89, 164, 0.3);
}

.btn-outline-secondary {
  border: 2px solid #e2e8f0;
  color: #64748b;
}

.btn-outline-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.alert-info {
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 12px;
  color: #0a59a4;
}

.loading-container {
  padding: 3rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

@media (max-width: 768px) {
  .info-cards {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .btn-lg.ms-3 {
    margin-left: 0 !important;
  }
}
</style>